import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import { useConfiguration } from "@/api/configuration";
import { usePreferences, useUpdatePreferences } from "@/api/me";
import { useEffectiveWeekStart } from "@/api/useEffectiveWeekStart";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const schema = z.object({
  language: z.enum(["en", "de"]),
  displayWeekStart: z.number().int().min(1).max(7),
});

type FormValues = z.infer<typeof schema>;

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export default function PreferencesCard() {
  const { t } = useTranslation();
  const prefsQuery = usePreferences();
  const configQuery = useConfiguration();
  const effectiveWeekStart = useEffectiveWeekStart();
  const update = useUpdatePreferences();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      language: (prefsQuery.data?.language as "en" | "de" | undefined) ?? "de",
      displayWeekStart: effectiveWeekStart,
    },
  });

  useEffect(() => {
    if (prefsQuery.data) {
      form.reset({
        language: prefsQuery.data.language as "en" | "de",
        displayWeekStart:
          prefsQuery.data.displayWeekStart ?? configQuery.data?.weekStart ?? 1,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when the user's own prefs change; a background tenant-config refetch must not wipe in-flight edits
  }, [prefsQuery.data, form]);

  const onSubmit = (values: FormValues) => {
    update.mutate(values, {
      onSuccess: () => toast.success(t("profile.preferencesCard.saved")),
      onError: () => toast.error(t("profile.preferencesCard.saveFailed")),
    });
  };

  const tenantDayLabel = configQuery.data
    ? t(`profile.weekdays.${configQuery.data.weekStart}` as const)
    : "";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.preferencesCard.title")}</CardTitle>
        <CardDescription>
          {t("profile.preferencesCard.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("profile.preferencesCard.languageLabel")}
                  </FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger
                        className="w-64"
                        aria-label={t("profile.preferencesCard.languageLabel")}
                      >
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="de">
                        {t("profile.languages.de")}
                      </SelectItem>
                      <SelectItem value="en">
                        {t("profile.languages.en")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="displayWeekStart"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("profile.preferencesCard.weekStartLabel")}
                  </FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(parseInt(v, 10))}
                    value={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger
                        className="w-64"
                        aria-label={t("profile.preferencesCard.weekStartLabel")}
                      >
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {WEEKDAYS.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {t(`profile.weekdays.${d}` as const)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {t("profile.preferencesCard.weekStartHint", {
                      day: tenantDayLabel,
                    })}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!form.formState.isDirty || update.isPending}
              >
                {t("common.buttons.save")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
